import React from "react";
import CustomButton from "components/common/Button/CustomButton";

const formBadge = (form) => {
  if (form.template_mode === "custom") {
    return {
      label: "Using Custom",
      className: "bg-teal-50 text-teal-800 border-teal-200",
      dotClass: "bg-teal-500",
      borderClass: "border-teal-200 shadow-sm",
    };
  }
  if (form.has_custom) {
    return {
      label: "Using Default",
      className: "bg-blue-50 text-blue-800 border-blue-200",
      dotClass: "bg-blue-400",
      borderClass: "border-blue-200",
    };
  }
  return {
    label: "Default (auto)",
    className: "bg-gray-50 text-gray-600 border-gray-200",
    dotClass: "bg-gray-300",
    borderClass: "border-gray-200",
  };
};

const formDescription = (form) => {
  if (form.template_mode === "custom") {
    return "Submissions of this form download with your custom DomPDF layout and field mappings.";
  }
  if (form.has_custom) {
    return "Downloads use the built-in default DomPDF. Your custom draft is saved — switch to Custom in PDF settings to use it.";
  }
  return "No custom layout saved. Downloads for this form use the built-in default DomPDF.";
};

const OverviewTab = ({ forms = [], onConfigureForm }) => {
  const usingCustomCount = forms.filter(
    (f) => f.template_mode === "custom"
  ).length;
  const totalCount = forms.length;
  const allUsingCustom = totalCount > 0 && usingCustomCount === totalCount;
  const noneUsingCustom = usingCustomCount === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900">
            PDF settings by form
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Each Service Agreement form can use the built-in default DomPDF or a
            custom layout you design. When staff download a PDF, the active mode
            for that form is used. Switch between default and custom anytime —
            your custom draft is kept when you revert to default.
          </p>
        </div>

        {totalCount > 0 ? (
          <div className="flex flex-wrap gap-3 shrink-0">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 min-w-[110px]">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Forms
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {totalCount}
              </p>
            </div>
            <div
              className={`rounded-md border px-4 py-3 min-w-[110px] ${
                usingCustomCount > 0
                  ? "border-teal-200 bg-teal-50"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Using Custom
              </p>
              <p
                className={`mt-1 text-2xl font-semibold ${
                  usingCustomCount > 0 ? "text-teal-800" : "text-gray-900"
                }`}
              >
                {usingCustomCount}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {totalCount === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
          <p className="text-base font-medium text-gray-900">
            No Service Agreement forms found
          </p>
          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            Publish an NDIS Service Agreement form for your organisation first.
            It will appear here so you can set up its PDF.
          </p>
        </div>
      ) : (
        <>
          <div
            className={`rounded-md border px-4 py-3 text-sm ${
              allUsingCustom
                ? "border-teal-200 bg-teal-50 text-teal-900"
                : noneUsingCustom
                ? "border-blue-100 bg-blue-50 text-blue-900"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            {allUsingCustom
              ? "All Service Agreement forms are using a custom DomPDF layout."
              : noneUsingCustom
              ? "All forms use the built-in default DomPDF. Create a custom layout in PDF settings for any form — you can switch back to default without losing your draft."
              : `${usingCustomCount} of ${totalCount} forms use a custom DomPDF layout. The rest use the built-in default.`}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {forms.map((form) => {
              const badge = formBadge(form);
              return (
                <div
                  key={form.id}
                  className={`rounded-md border bg-white p-4 flex flex-col gap-4 ${badge.borderClass}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex h-2.5 w-2.5 rounded-full shrink-0 ${badge.dotClass}`}
                          aria-hidden
                        />
                        <h4 className="text-sm font-semibold text-gray-900 break-words">
                          {form.title}
                        </h4>
                      </div>
                      {form.slug ? (
                        <p className="mt-1 text-xs text-gray-500 truncate">
                          {form.slug}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className={`shrink-0 text-xs font-medium px-2.5 py-1 rounded-full border ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 flex-1">
                    {formDescription(form)}
                  </p>

                  <div className="pt-1 border-t border-gray-100">
                    <CustomButton
                      label={
                        form.has_custom || form.template_mode === "custom"
                          ? "Edit PDF settings"
                          : "Set up PDF settings"
                      }
                      variant={
                        form.template_mode === "custom" ? "secondary" : "primary"
                      }
                      clickHandler={() =>
                        onConfigureForm && onConfigureForm(form.id)
                      }
                      cssClass="w-full sm:w-auto"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default OverviewTab;
